import { useCallback, useContext, useEffect, useState } from 'react';

import * as Sentry from '@sentry/browser';

import { getCookie } from '@atlassian/browser-storage-controls';

import { HorizontalNavContext } from '../components/horizontal-nav/horizontal-nav-context';
import urls from '../urls';

export const usePref: (
  prefKey: string
) => [string, (value: string) => undefined, boolean] = prefKey => {
  const { currentUser } = useContext(HorizontalNavContext);
  const [isLoading, setIsLoading] = useState(false);
  const [prefValue, setPrefValue] = useState<string>('');

  useEffect(() => {
    if (!currentUser?.uuid || !prefKey) return undefined;

    setIsLoading(true);
    fetch(urls.api.internal.userPreference(currentUser.uuid, prefKey))
      .then(res => res.json())
      .then((val: string) => setPrefValue(val))
      .catch(error => Sentry.captureException(error))
      .finally(() => setIsLoading(false));

    return undefined;
  }, [currentUser?.uuid, prefKey]);

  const updatePref = useCallback(
    (value: string) => {
      if (!currentUser?.uuid || !prefKey) return undefined;

      const token = getCookie('csrftoken');
      fetch(urls.api.internal.userPreference(currentUser.uuid, prefKey), {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRFToken': token || '',
        },
        body: JSON.stringify({ value }),
      }).catch(error => Sentry.captureException(error));
      return undefined;
    },
    [currentUser?.uuid, prefKey]
  );

  return [prefValue, updatePref, isLoading];
};
